/**
 * LE DÉCOUPAGE DE LA COURBE D'UNE ALARME : UNE FONCTION PURE.
 * ============================================================================
 *
 * INTENTION : la courbe ne ment pas. Un trou de mesure se voit comme un trou,
 * jamais comme une ligne tirée par-dessus ni comme un zéro.
 *
 * Les points viennent de `bilan.points_recents[{t, v}]` et la cadence de
 * `bilan.attendu_entre_points_s` (liste blanche de la façade,
 * `alarmesv2.episodes.js`). Règles :
 *  • un point sans instant lisible ou sans valeur finie n'est jamais dessiné
 *    (il est compté dans `ecartes`) ; une valeur `null` est une mesure absente :
 *    elle coupe la courbe, et l'intervalle se dit « pas de mesure » ;
 *  • deux points séparés de plus de `TOLERANCE_CADENCE` fois la cadence
 *    attendue appartiennent à deux segments, et l'intervalle est un trou
 *    (règle de la maquette, `courbeSVG`, S:7085-7095) ;
 *  • cadence inconnue : on ne sait pas où sont les trous, donc on ne tire
 *    aucune ligne : chaque point est son propre segment, et aucun trou n'est
 *    supposé ;
 *  • rien n'est interpolé, rien n'est mis à 0 : une valeur dessinée est une
 *    valeur servie.
 *
 * `resumerLaCourbe` dit la même chose en toutes lettres (`resumeCourbe`,
 * S:7192-7214) : c'est le texte du lecteur d'écran et le résumé sous la courbe.
 */
import type { Traduire } from "@modules/alarmes-v2/i18n/enregistrer"
import { formaterDuree, formaterInstant, formaterMesure } from "./mesure"

/** Un point tel que la façade le sert. */
export interface PointServi {
    t?: string | null
    v?: number | null
}

/** Un point lu : son instant en millisecondes et sa valeur. */
export interface PointLu {
    ms: number
    v: number
}

/** Un intervalle sans mesure, en millisecondes. */
export interface Trou {
    debut: number
    fin: number
}

/** Le découpage d'une série. */
export interface Decoupage {
    /** Les segments à tracer, chacun d'au moins un point. */
    segments: PointLu[][]
    /** Les intervalles « pas de mesure ». */
    trous: Trou[]
    /** Les points servis illisibles, jamais dessinés. */
    ecartes: number
    /** Faux quand la cadence attendue n'est pas servie. */
    cadenceConnue: boolean
}

/**
 * Au-delà de cette part de la cadence, deux points sont séparés par un trou
 * (maquette, `attendu * 1.5`). Un point en léger retard ne coupe pas la courbe.
 */
export const TOLERANCE_CADENCE = 1.5

/** Un instant ISO lisible, en millisecondes, ou `null`. */
function instant(t: unknown): number | null {
    if (typeof t !== "string" || t === "") return null
    const ms = new Date(t).getTime()
    return Number.isNaN(ms) ? null : ms
}

/**
 * Découpe une série en segments et en trous.
 * @param points - `bilan.points_recents`
 * @param attenduEntrePointsS - `bilan.attendu_entre_points_s`, en secondes
 * @param jusqua - la fin de la fenêtre (ms) : au-delà de la dernière mesure, un intervalle plus long que la cadence se dit sans mesure
 */
export function decouperLaCourbe(
    points: PointServi[] | null | undefined,
    attenduEntrePointsS: number | null | undefined,
    jusqua?: number | null
): Decoupage {
    const cadenceConnue =
        typeof attenduEntrePointsS === "number" &&
        Number.isFinite(attenduEntrePointsS) &&
        attenduEntrePointsS > 0
    const ecartMax = cadenceConnue
        ? attenduEntrePointsS * 1000 * TOLERANCE_CADENCE
        : 0

    // Chaque point servi : une mesure, une absence (valeur nulle), ou illisible.
    let ecartes = 0
    const lus: { ms: number; v: number | null }[] = []
    for (const p of Array.isArray(points) ? points : []) {
        const ms = instant(p?.t)
        const v = p?.v
        if (ms === null) {
            ecartes += 1
        } else if (v === null || v === undefined) {
            lus.push({ ms, v: null })
        } else if (typeof v === "number" && Number.isFinite(v)) {
            lus.push({ ms, v })
        } else {
            ecartes += 1
        }
    }
    lus.sort((x, y) => x.ms - y.ms)

    const segments: PointLu[][] = []
    const trous: Trou[] = []
    let courant: PointLu[] = []
    /** La dernière vraie mesure, et vrai si une absence la suit. */
    let precedent: PointLu | null = null
    let absenceDepuis = false

    for (const p of lus) {
        if (p.v === null) {
            absenceDepuis = true
            continue
        }
        const point: PointLu = { ms: p.ms, v: p.v }
        const coupe =
            precedent !== null &&
            (!cadenceConnue ||
                absenceDepuis ||
                point.ms - precedent.ms > ecartMax)
        if (coupe && courant.length > 0) {
            segments.push(courant)
            courant = []
            // Un trou ne se dit que quand la cadence ou une absence servie le prouve.
            if (precedent && (cadenceConnue || absenceDepuis)) {
                trous.push({ debut: precedent.ms, fin: point.ms })
            }
        }
        courant.push(point)
        precedent = point
        absenceDepuis = false
    }
    if (courant.length > 0) segments.push(courant)

    // Après la dernière mesure, jusqu'à la fin de la fenêtre.
    if (
        precedent &&
        cadenceConnue &&
        typeof jusqua === "number" &&
        jusqua - precedent.ms > ecartMax
    ) {
        trous.push({ debut: precedent.ms, fin: jusqua })
    }

    return { segments, trous, ecartes, cadenceConnue }
}

/** Le côté du seuil où la mesure sort : il choisit « au plus haut » ou « au plus bas ». */
export type CoteDuSeuil = "haut" | "bas" | null

/**
 * Le côté du seuil réellement franchi (T2 §2.2 : `valeur_extreme` est le
 * maximum quand `borne_haute` est franchie, le minimum quand c'est
 * `borne_basse`). Une seule borne : son côté. Deux bornes : l'extrême servi
 * dit le côté ; sans extrême servi, les points le disent s'ils ne sortent que
 * d'un côté. Un côté qu'on ne peut pas prouver vaut `null` : le résumé ne dit
 * alors ni « au plus haut » ni « au plus bas ».
 * @param bornes - `seuils_figes.borne_haute` et `borne_basse`
 * @param extreme - `bilan.valeur_extreme`
 * @param points - les points lus de la courbe
 */
export function coteFranchi(
    bornes: { haute?: number | null; basse?: number | null },
    extreme: number | null | undefined,
    points: PointLu[]
): CoteDuSeuil {
    const fini = (x: unknown): x is number =>
        typeof x === "number" && Number.isFinite(x)
    const haute = fini(bornes.haute) ? bornes.haute : null
    const basse = fini(bornes.basse) ? bornes.basse : null
    if (haute === null && basse === null) return null
    if (basse === null) return "haut"
    if (haute === null) return "bas"
    if (fini(extreme)) {
        if (extreme > haute) return "haut"
        if (extreme < basse) return "bas"
        return null
    }
    const auDessus = points.some((p) => p.v > haute)
    const auDessous = points.some((p) => p.v < basse)
    if (auDessus === auDessous) return null
    return auDessus ? "haut" : "bas"
}

/** Ce qu'il faut pour résumer une courbe. */
export interface OptionsResume {
    t: Traduire
    unite?: string | null
    cote?: CoteDuSeuil
    /** `bilan.valeur_extreme` et son instant, s'ils sont servis. */
    extreme?: number | null
    extremeLe?: string | null
    /** `bilan.duree_hors_seuil_s`. */
    dureeHorsSeuilS?: number | null
}

/** Un instant en millisecondes, dit comme ailleurs dans l'écran. */
const dire = (ms: number, t: Traduire) =>
    formaterInstant(new Date(ms).toISOString(), t) ?? ""

/**
 * La courbe en toutes lettres : la dernière mesure, l'extrême du côté du
 * seuil, la durée hors seuil, puis chaque intervalle sans mesure.
 * @param d - le découpage de la courbe
 * @param o - l'unité, le côté du seuil, le bilan servi
 */
export function resumerLaCourbe(d: Decoupage, o: OptionsResume): string {
    const { t } = o
    const points = d.segments.flat()
    if (points.length === 0) return t("alarmesV2.courbe.aucunPoint")
    const derniere = points.reduce((x, y) => (y.ms > x.ms ? y : x))
    let phrase = t("alarmesV2.courbe.resumeDerniere", {
        valeur: formaterMesure(derniere.v, o.unite, t),
    })
    if (o.cote) {
        const servi =
            typeof o.extreme === "number" && Number.isFinite(o.extreme)
                ? { v: o.extreme, quand: formaterInstant(o.extremeLe, t) }
                : null
        const lu = points.reduce((x, y) =>
            o.cote === "bas" ? (y.v < x.v ? y : x) : y.v > x.v ? y : x
        )
        const extreme = servi ?? { v: lu.v, quand: dire(lu.ms, t) }
        phrase += t(
            o.cote === "bas"
                ? "alarmesV2.courbe.resumeBas"
                : "alarmesV2.courbe.resumeHaut",
            {
                valeur: formaterMesure(extreme.v, o.unite, t),
                quand: extreme.quand ?? "",
            }
        )
    }
    const duree =
        typeof o.dureeHorsSeuilS === "number" && o.dureeHorsSeuilS > 0
            ? formaterDuree(o.dureeHorsSeuilS, t)
            : null
    if (duree) phrase += t("alarmesV2.courbe.resumeHorsSeuil", { duree })
    if (d.trous.length > 0) {
        const liste = d.trous
            .map((x) =>
                t("alarmesV2.courbe.resumeTrou", {
                    debut: dire(x.debut, t),
                    fin: dire(x.fin, t),
                })
            )
            .join(t("alarmesV2.courbe.resumeTrouSuivant"))
        phrase += t("alarmesV2.courbe.resumeTrous", { liste })
    }
    if (!d.cadenceConnue && points.length > 0)
        phrase += t("alarmesV2.courbe.resumeCadenceInconnue")
    return phrase + t("alarmesV2.courbe.resumeFin")
}
